import dot from 'dot-prop-immutable';

import type {
  PreparedFeature,
  PreparedFeedbackItem,
  PreparedViewerItem,
} from '@frontend/partviewer';
import type { STOCK_TYPES } from '@frontend/partviewer/src/constants';
import type { Comparison } from '@frontend/partviewer/src/renderer/caliper-utils';
import type {
  LiteObject,
  LitState,
} from '@frontend/partviewer/src/renderer/load-utils';
import type {
  Highlight,
  MetaData,
  Selection,
} from '@frontend/partviewer/src/renderer/renderer-component';
import type { RawResultForCreation } from '@frontend/partviewer/src/renderer/stock-utils';

import { UNITS, WEIGHT_UNITS } from 'constants/all';

import { types as appTypes, type AppAction } from 'store/app/actions';
import type { RootState } from 'store/configureStore';
import type { ActionOfType } from 'store/types';

import {
  getEncodedCADViewerState,
  decodeEncodedCADViewerState,
} from '../utils';
import {
  types,
  type CadViewerAction,
  type CadViewerForceView,
  type CadViewerInterrogationInfoPayload,
  type EncodedCadViewerState,
} from './actions';

const DIMENSIONAL_PRECISION = 'partviewer:dimensionalPrecision';
const DISPLAY_UNITS = 'partviewer:displayUnits';

type DisplayUnit = (typeof UNITS)[keyof typeof UNITS];

type CrossSectionNormal = {
  x: number;
  y: number;
  z: number;
};

type CadViewerSelectorSelection = Selection & {
  cadViewerState?: EncodedCadViewerState;
};

export type CadViewerState = {
  // array of body viewer ids that drive interaction such as wireframe and dimensions
  activeBodies: string[];
  activeGeometricPartFileUuids: string[];
  // an object is a single selected entity (faces, edges, vertices) for use in caliper tool, these are historical, drive historicalCaliperObjects in viewer-component state, historicalCaliperOptions are used for chat annotations
  caliperObjects: LiteObject[];
  // caliper tool selections are the selected object as well as its viewer state at the moment of selection, drive caliperObjects in viewer-component state
  caliperToolSelections: LiteObject[];
  captureViewerState: boolean;
  capturedCaliperCADState: EncodedCadViewerState | Record<string, never>;
  // state we capture every time a caliper measurement selection is made
  capturedCaliperState: LitState | Record<string, never>;
  // float value of current measurement result
  currentMeasurement: number | null;
  // decimal precision of displayed measurements
  dimensionalPrecision: number;
  // drives the values shown in measurements, dimensions, and meta data relative to actual rendering entity sizes
  displayScale: number;
  displayUnits: DisplayUnit;
  displayWeightUnits: string;
  // determines interactive state with assembly tree
  enabledBodies: string[];
  // determines what values are expanded for the asssembly tree
  expandedBodies: string[];
  // object of assembly instance id to a float factor value to dictate how much to translate children
  explodedState: Record<string, number>;
  // object of feature info being requested to be snapped to
  featureSnapTo: PreparedViewerItem | null;
  // array of body viewer ids that drive zoomTo and visibility inversion logic
  focusedBodies: string[];
  // determines what set of manufacturability feedback/features is being extracted
  focusedUuid: string | null;
  // boolean value for overriding natural focusBodies changing behavior to trigger focus action
  forceFocus: boolean;
  // object that tells us when to perform visual snap to actions like zoom to fit and view normal to
  forceView: CadViewerForceView;
  //false until partviewer says there is PMI
  hasPMIAnnotations: boolean;
  // highlight info from selected features and feedback
  highlight: Highlight[];
  // viewer id of currently hovered body in the assembly tree
  hoverBody: string | null;
  interrogationInfo: CadViewerInterrogationInfoPayload;
  isDimensionToolActive: boolean;
  isExplodedViewToolActive: boolean;
  isFocusedViewActive: boolean;
  isGeometricInteractionReady: boolean;
  isHighlightViewActive: boolean;
  isMeasurementToolActive: boolean;
  isOptimalBoundingBoxToolActive: boolean;
  isPMIViewActive: boolean;
  isSelectorToolActive: boolean;
  isWireFrameToolActive: boolean;
  crossSectionEnabled: boolean;
  crossSectionInverted: boolean;
  crossSectionPickMode: boolean;
  crossSectionPosition: number;
  crossSectionNormal: CrossSectionNormal;
  materialStockResults: RawResultForCreation<STOCK_TYPES>[];
  // meta data around a measurement giving us angle and description of coincident relationship
  measurementComparison: Comparison | null;
  metaData: MetaData;
  nativeUnits: DisplayUnit;
  // array for the sake of refocusing
  prevFocusedBodies: string[];
  renderNativeColors: boolean;
  // array of body viewer ids that drive navigational highlighting
  selectedBodies: string[];
  // a feature is defined by our backend interrogation, such as a hole or pocket, and consists of a collection of entities (faces, edges, vertices)
  selectedFeatures: PreparedFeature[];
  selectedManufacturabilityFeedback: PreparedFeedbackItem[];
  // an object is a single selected entity (faces, edges, vertices), these are historical, drive historicalSelectedObjects in viewer-component state
  selectedObjects: LiteObject[];
  // an object that tracks the cumulative data of selected objects (area, edge length)
  selectorCumulativeInfo: {
    area?: number;
    length?: number;
  };
  selectorToolSelections: CadViewerSelectorSelection[]; // selector tools selections are the selected object as well as its viewer state at the moment of selection, drive selectedObjects in viewer-component state
  // array of keys for bodies tied to feature and feedback selections to render when selected
  showAdditionalKeys: string[];
  // array of keys for bodies tied to feature and feedback selections to render when explicitly selected
  showAdditionalKeysExplicit: string[];
  //body ids that are temporarily simplified in the part viewer
  temporarilySimplified: Set<string>;
  //part ids of bodies that are temporarily simplified in the part viewer
  temporarilySimplifiedParts: Set<string>;
  //part ids that are too complex for the part viewer to render in full detail
  tooComplex: Set<string>;
  // array of part file uuids that are awaiting being loaded in the part viewer
  viewerPendingPartFileUuids: string[];
  // array of part file uuids that have been loaded in the viewer and are available for interaction
  viewerReadyPartFileUuids: string[];
  viewerState: LitState | null;
  showDxfControls: boolean;
  // array of object ids, assemblies or bodies, that tell the viewer what to render
  visibleBodies: string[];
  // array of top level object ids that describe renderings of stock options
  visibleStockResultKeys: string[];
};

const initialState = (): CadViewerState => ({
  activeBodies: [],
  activeGeometricPartFileUuids: [],
  caliperObjects: [],
  caliperToolSelections: [],
  captureViewerState: false,
  capturedCaliperCADState: {},
  capturedCaliperState: {},
  currentMeasurement: null,
  dimensionalPrecision: +localStorage.getItem(DIMENSIONAL_PRECISION) || 3,
  displayScale: 1.0,
  displayUnits: (localStorage.getItem(DISPLAY_UNITS) ??
    UNITS.IN) as DisplayUnit,
  displayWeightUnits: WEIGHT_UNITS.LB,
  enabledBodies: [],
  expandedBodies: [],
  explodedState: {},
  featureSnapTo: null,
  focusedBodies: [],
  focusedUuid: null,
  forceFocus: false,
  forceView: null,
  hasPMIAnnotations: false,
  highlight: [],
  hoverBody: null,
  interrogationInfo: {},
  isDimensionToolActive: false,
  isExplodedViewToolActive: false,
  isFocusedViewActive: false,
  isGeometricInteractionReady: true,
  isHighlightViewActive: false,
  isMeasurementToolActive: false,
  isOptimalBoundingBoxToolActive: false,
  isPMIViewActive: false,
  isSelectorToolActive: false,
  isWireFrameToolActive: false,
  crossSectionEnabled: false,
  crossSectionInverted: false,
  crossSectionPickMode: false,
  crossSectionPosition: 0.5,
  crossSectionNormal: { x: 0, y: 0, z: -1 },
  materialStockResults: [],
  measurementComparison: null,
  metaData: {},
  nativeUnits: UNITS.MM,
  prevFocusedBodies: [],
  renderNativeColors: false,
  selectedBodies: [],
  selectedFeatures: [],
  selectedManufacturabilityFeedback: [],
  selectedObjects: [],
  selectorCumulativeInfo: {},
  selectorToolSelections: [],
  showAdditionalKeys: [],
  showAdditionalKeysExplicit: [],
  temporarilySimplified: new Set(),
  temporarilySimplifiedParts: new Set(),
  tooComplex: new Set(),
  viewerPendingPartFileUuids: [],
  viewerReadyPartFileUuids: [],
  viewerState: null,
  showDxfControls: false,
  visibleBodies: [],
  visibleStockResultKeys: [],
});

//initialState is a factory so that state is reset everytime
const reducer = (
  state: CadViewerState = initialState(),
  action:
    | CadViewerAction
    | ActionOfType<AppAction, typeof appTypes.LOCATION_CHANGE>,
): CadViewerState => {
  switch (action.type) {
    case appTypes.LOCATION_CHANGE:
      return initialState();
    case types.CAD_VIEWER_APPEND_VISIBLE_BODIES: {
      const { visibleBodies: visibleBodiesToAppend } = action.payload;
      return {
        ...state,
        visibleBodies: Array.from(
          new Set([...state.visibleBodies, ...visibleBodiesToAppend]),
        ),
      };
    }
    case types.CAD_VIEWER_RECEIVE_ACTIVE_BODIES: {
      const { activeBodies } = action.payload;
      return {
        ...state,
        activeBodies,
      };
    }
    case types.CAD_VIEWER_RECEIVE_DISPLAY_SCALE: {
      const { displayScale } = action.payload;
      return {
        ...state,
        displayScale,
      };
    }
    case types.CAD_VIEWER_RECEIVE_DISPLAY_UNITS: {
      const { displayUnits } = action.payload;
      localStorage.setItem(DISPLAY_UNITS, displayUnits);
      return {
        ...state,
        displayUnits,
      };
    }
    case types.CAD_VIEWER_RECEIVE_DIMENSIONAL_PRECISION: {
      const { dimensionalPrecision } = action.payload;
      localStorage.setItem(DIMENSIONAL_PRECISION, String(dimensionalPrecision));
      return {
        ...state,
        dimensionalPrecision,
      };
    }
    case types.CAD_VIEWER_RECEIVE_DISPLAY_WEIGHT_UNITS: {
      const { displayWeightUnits } = action.payload;
      return {
        ...state,
        displayWeightUnits,
      };
    }
    case types.CAD_VIEWER_RECEIVE_ENABLED_BODIES: {
      const { enabledBodies } = action.payload;
      return {
        ...state,
        enabledBodies,
      };
    }
    case types.CAD_VIEWER_RECEIVE_EXPANDED_BODIES: {
      const { expandedBodies } = action.payload;
      return {
        ...state,
        expandedBodies,
      };
    }
    case types.FEATURE_SNAP_TO_RECEIVE: {
      const { feature: featureSnapTo } = action.payload;
      return {
        ...state,
        featureSnapTo,
      };
    }
    case types.CAD_VIEWER_RECEIVE_FOCUSED_BODIES: {
      const { focusedBodies } = action.payload;
      return {
        ...state,
        prevFocusedBodies: state.focusedBodies.slice(),
        focusedBodies,
      };
    }
    case types.CAD_VIEWER_RECEIVE_FOCUSED_UUID: {
      const { focusedUuid } = action.payload;
      return {
        ...state,
        focusedUuid,
      };
    }
    case types.CAD_VIEWER_RECEIVE_FORCE_FOCUS: {
      const { forceFocus } = action.payload;
      return {
        ...state,
        forceFocus,
      };
    }
    case types.CAD_VIEWER_RECEIVE_FORCE_VIEW: {
      const { forceView } = action.payload;
      return {
        ...state,
        forceView,
      };
    }
    case types.CAD_VIEWER_RECEIVE_INTERROGATION_INFO: {
      const { documentHash, interrogationFileId, task } = action.payload;
      return {
        ...state,
        interrogationInfo: {
          documentHash,
          interrogationFileId,
          task,
        },
      };
    }
    case types.CAD_VIEWER_RECEIVE_HIGHLIGHT: {
      const { highlight } = action.payload;
      return {
        ...state,
        highlight,
      };
    }
    case types.CAD_VIEWER_RECEIVE_HOVER_BODY: {
      const { hoverBody } = action.payload;
      return {
        ...state,
        hoverBody,
      };
    }
    case types.CAD_VIEWER_RECEIVE_META_DATA: {
      const { metaData } = action.payload;
      return {
        ...state,
        metaData,
      };
    }
    case types.CAD_VIEWER_RECEIVE_NATIVE_UNITS: {
      const { nativeUnits } = action.payload;
      return {
        ...state,
        nativeUnits,
      };
    }
    case types.CAD_VIEWER_RECEIVE_SELECTED_BODIES: {
      const { selectedBodies } = action.payload;
      return {
        ...state,
        selectedBodies,
      };
    }
    case types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS: {
      const { showAdditionalKeys } = action.payload;
      return {
        ...state,
        showAdditionalKeys,
      };
    }
    case types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT: {
      const { showAdditionalKeysExplicit } = action.payload;
      return {
        ...state,
        showAdditionalKeysExplicit,
      };
    }
    case types.CAD_VIEWER_RECEIVE_TEMPORARILY_SIMPLIFIED: {
      const { temporarilySimplified, temporarilySimplifiedParts } =
        action.payload;
      return {
        ...state,
        temporarilySimplified,
        temporarilySimplifiedParts,
      };
    }
    case types.CAD_VIEWER_RECEIVE_TOO_COMPLEX: {
      const { tooComplex } = action.payload;
      return {
        ...state,
        tooComplex,
      };
    }
    case types.CAD_VIEWER_RECEIVE_SHOW_DXF_CONTROLS: {
      const { showDxfControls } = action.payload;
      return {
        ...state,
        showDxfControls,
        ...(showDxfControls
          ? {
              crossSectionEnabled: false,
              crossSectionPickMode: false,
              isWireFrameToolActive: false,
              isHighlightViewActive: false,
              isFocusedViewActive: false,
            }
          : {}),
      };
    }
    case types.CAD_VIEWER_RECEIVE_VISIBLE_BODIES: {
      const { visibleBodies } = action.payload;
      return {
        ...state,
        visibleBodies,
      };
    }
    case types.CAD_VIEWER_REMOVE_VISIBLE_BODIES: {
      const { viewerIds } = action.payload;
      return {
        ...state,
        visibleBodies: state.visibleBodies.filter(
          (viewerId) => !viewerIds.includes(viewerId),
        ),
      };
    }
    case types.CAD_VIEWER_SET_GEOMETRIC_ENTITIES: {
      const { showAdditionalKeysExplicit } = action.payload;
      return {
        ...state,
        selectedFeatures: [],
        selectedManufacturabilityFeedback: [],
        showAdditionalKeys: [],
        showAdditionalKeysExplicit: showAdditionalKeysExplicit || [],
        highlight: [],
      };
    }
    case types.CAD_VIEWER_SET_RENDER_NATIVE_COLORS: {
      const { renderNativeColors } = action.payload;
      return {
        ...state,
        renderNativeColors,
      };
    }
    case types.DIMENSION_TOOL_TOGGLE: {
      const nextDimensionsToolState = !state.isDimensionToolActive;
      const nextExplodedToolState = nextDimensionsToolState
        ? false
        : state.isExplodedViewToolActive;
      const nextExplodedState = nextDimensionsToolState
        ? {}
        : state.explodedState;
      return {
        ...state,
        explodedState: nextExplodedState,
        isDimensionToolActive: nextDimensionsToolState,
        isExplodedViewToolActive: nextExplodedToolState,
        isOptimalBoundingBoxToolActive: false,
      };
    }
    case types.CROSS_SECTION_SET_ENABLED: {
      const { enabled: nextCrossSectionEnabled } = action.payload;
      return {
        ...state,
        ...(nextCrossSectionEnabled
          ? {
              explodedState: {},
              isExplodedViewToolActive: false,
            }
          : {}),
        crossSectionEnabled: nextCrossSectionEnabled,
        crossSectionPickMode: nextCrossSectionEnabled
          ? state.crossSectionPickMode
          : false,
        ...(nextCrossSectionEnabled
          ? {
              crossSectionPosition: 0.5,
              crossSectionNormal: { x: 0, y: 0, z: -1 },
              crossSectionInverted: false,
            }
          : {}),
      };
    }
    case types.CROSS_SECTION_SET_POSITION: {
      const { position } = action.payload;
      return {
        ...state,
        crossSectionPosition: Math.min(1, Math.max(0, Number(position))),
      };
    }
    case types.CROSS_SECTION_TOGGLE_INVERTED:
      return {
        ...state,
        crossSectionInverted: !state.crossSectionInverted,
      };
    case types.CROSS_SECTION_SET_PICK_MODE: {
      const { pickMode } = action.payload;
      // Face picking requires shaded faces; wireframe hides them.
      const nextPick = !!pickMode && !state.isWireFrameToolActive;
      return {
        ...state,
        crossSectionPickMode: nextPick,
        ...(nextPick
          ? {
              isSelectorToolActive: false,
              isMeasurementToolActive: false,
            }
          : {}),
      };
    }
    case types.CROSS_SECTION_SET_NORMAL: {
      const { normal: crossSectionNormal, position } = action.payload;
      return {
        ...state,
        crossSectionNormal,
        ...(position != null && Number.isFinite(Number(position))
          ? {
              crossSectionPosition: Math.min(1, Math.max(0, Number(position))),
            }
          : {}),
      };
    }
    case types.OPTIMAL_BOUNDING_BOX_TOOL_TOGGLE: {
      const nextOptimalBoxToolState = !state.isOptimalBoundingBoxToolActive;
      const nextExplodedToolState = nextOptimalBoxToolState
        ? false
        : state.isExplodedViewToolActive;
      const nextExplodedState = nextOptimalBoxToolState
        ? {}
        : state.explodedState;
      return {
        ...state,
        explodedState: nextExplodedState,
        isOptimalBoundingBoxToolActive: nextOptimalBoxToolState,
        isDimensionToolActive: false,
        isExplodedViewToolActive: nextExplodedToolState,
      };
    }
    case types.EXPLODED_VIEW_TOOL_DISABLE:
      return {
        ...state,
        isExplodedViewToolActive: false,
        explodedState: {},
      };
    case types.EXPLODED_VIEW_TOOL_RECEIVE_EXPLODED_STATE: {
      const { assemblyInstanceId, factor } = action.payload;
      return dot.set(state, `explodedState.${assemblyInstanceId}`, factor);
    }
    case types.EXPLODED_VIEW_TOOL_TOGGLE: {
      const nextExplodedToolState = !state.isExplodedViewToolActive;
      const nextDimensionsToolState = nextExplodedToolState
        ? false
        : state.isDimensionToolActive;
      const nextOptimalBoxToolState = nextExplodedToolState
        ? false
        : state.isOptimalBoundingBoxToolActive;
      return {
        ...state,
        isExplodedViewToolActive: nextExplodedToolState,
        isDimensionToolActive: nextDimensionsToolState,
        isOptimalBoundingBoxToolActive: nextOptimalBoxToolState,
        ...(nextExplodedToolState
          ? {
              crossSectionEnabled: false,
              crossSectionPickMode: false,
              crossSectionPosition: 0.5,
              crossSectionNormal: { x: 0, y: 0, z: -1 },
              crossSectionInverted: false,
            }
          : {}),
      };
    }
    case types.FEATURE_SELECTION_RECEIVE: {
      const { features: selectedFeatures } = action.payload;
      return {
        ...state,
        selectedFeatures,
      };
    }
    case types.FOCUSED_VIEW_TOGGLE:
      return {
        ...state,
        isFocusedViewActive: !state.isFocusedViewActive,
      };
    case types.GEOMETRIC_PART_FILE_BULK_PREPARE: {
      const { partFileUuids } = action.payload;
      const newActiveGeometricPartFileUuids = partFileUuids.filter(
        (partFileUuid) =>
          !state.activeGeometricPartFileUuids.includes(partFileUuid),
      );
      return !newActiveGeometricPartFileUuids.length
        ? state
        : {
            ...state,
            activeGeometricPartFileUuids: [
              ...state.activeGeometricPartFileUuids,
              ...newActiveGeometricPartFileUuids,
            ],
          };
    }
    case types.HIGHLIGHT_VIEW_TOGGLE:
      return {
        ...state,
        isHighlightViewActive: !state.isHighlightViewActive,
        isWireFrameToolActive: !state.isHighlightViewActive
          ? false
          : state.isWireFrameToolActive,
      };
    case types.MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE: {
      const { manufacturabilityFeedback: selectedManufacturabilityFeedback } =
        action.payload;
      return {
        ...state,
        selectedManufacturabilityFeedback,
      };
    }
    case types.MATERIAL_STOCK_RECEIVE_NEW_RESULT: {
      const { result } = action.payload;
      return {
        ...state,
        materialStockResults: state.materialStockResults.concat([result]),
      };
    }
    case types.MATERIAL_STOCK_RECEIVE_VISIBLE_KEYS: {
      const { resultKeys: visibleStockResultKeys } = action.payload;
      return {
        ...state,
        visibleStockResultKeys,
      };
    }
    case types.MEASUREMENT_TOOL_TOGGLE: {
      // measurement tool and selector tool cannot both be active at the same time
      const nextMeasurementToolState = !state.isMeasurementToolActive;
      const nextSelectorToolState = nextMeasurementToolState
        ? false
        : state.isSelectorToolActive;

      return {
        ...state,
        isMeasurementToolActive: nextMeasurementToolState,
        isSelectorToolActive: nextSelectorToolState,
        crossSectionPickMode: false,
        selectorCumulativeInfo: {},
        selectorToolSelections: [],
        selectedObjects: [],
        caliperObjects: [],
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
      };
    }
    case types.MEASUREMENT_TOOL_DISABLE:
      return {
        ...state,
        isMeasurementToolActive: false,
        currentMeasurement: null,
        caliperToolSelections: [],
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_ENABLE:
      // measurement tool and selector tool cannot both be active at the same time
      // clear measurement tool when enabling when previously disabled
      return {
        ...state,
        isMeasurementToolActive: true,
        isSelectorToolActive: false,
        crossSectionPickMode: false,
        caliperObjects: state.isMeasurementToolActive
          ? state.caliperObjects
          : [],
        caliperToolSelections: state.isMeasurementToolActive
          ? state.caliperToolSelections
          : [],
        currentMeasurement: state.isMeasurementToolActive
          ? state.currentMeasurement
          : null,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_COMPARISON: {
      const { comparison: measurementComparison } = action.payload;
      return {
        ...state,
        measurementComparison,
      };
    }
    case types.MEASUREMENT_TOOL_RECEIVE_SELECTIONS: {
      const {
        selections: caliperToolSelections,
        viewerState: capturedCaliperState,
      } = action.payload;
      return {
        ...state,
        capturedCaliperState,
        capturedCaliperCADState: getEncodedCADViewerState(state),
        caliperToolSelections,
        currentMeasurement: null,
        measurementComparison: null,
      };
    }
    case types.MEASUREMENT_TOOL_REMOVE_SELECTIONS:
      return {
        ...state,
        capturedCaliperState: {},
        capturedCaliperCADState: {},
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_MEASUREMENT: {
      const { distance: currentMeasurement } = action.payload;
      return {
        ...state,
        currentMeasurement,
      };
    }
    case types.MEASUREMENT_OBJECTS_MANAGER_RECEIVE_SELECTIONS: {
      const { caliperObjects } = action.payload;
      return {
        ...state,
        caliperObjects,
        measurementComparison: null,
      };
    }
    case types.MEASUREMENT_OBJECTS_MANAGER_REMOVE_SELECTIONS:
      return {
        ...state,
        caliperObjects: [],
        measurementComparison: null,
      };
    case types.PMI_VIEW_HIDE:
      return {
        ...state,
        isPMIViewActive: false,
      };
    case types.PMI_VIEW_SHOW:
      return {
        ...state,
        isPMIViewActive: state.hasPMIAnnotations,
      };
    case types.PMI_VIEW_TOGGLE:
      return {
        ...state,
        isPMIViewActive: state.hasPMIAnnotations && !state.isPMIViewActive,
      };
    case types.CAD_VIEWER_RECEIVE_HAS_PMI_ANNOTATIONS: {
      const { hasPMIAnnotations } = action.payload;
      return {
        ...state,
        hasPMIAnnotations,
      };
    }
    case types.RENDER_NATIVE_COLORS_TOGGLE:
      return {
        ...state,
        renderNativeColors: !state.renderNativeColors,
      };
    case types.SELECTED_OBJECTS_MANAGER_RECEIVE_SELECTIONS: {
      const { selections } = action.payload;
      return {
        ...state,
        selectedObjects: state.selectedObjects.concat(selections),
      };
    }
    case types.SELECTED_OBJECTS_MANAGER_REMOVE_ALL_SELECTIONS:
      return {
        ...state,
        selectedObjects: [],
      };
    case types.SELECTED_OBJECTS_MANAGER_REMOVE_SELECTIONS: {
      const { selections } = action.payload;
      let { selectedObjects } = state;
      selections.forEach((deselected) => {
        const isStep = deselected.obj.includes('|');
        if (isStep) {
          selectedObjects = selectedObjects.filter(
            (selectedObject) => selectedObject.obj !== deselected.obj,
          );
        } else {
          // stls
          selectedObjects = selectedObjects.filter(
            (selectedObject) => selectedObject.f !== deselected.f,
          );
        }
      });
      return {
        ...state,
        selectedObjects: [...selectedObjects],
      };
    }
    case types.SELECTOR_TOOL_BOX_SELECT: {
      const { selections: selectorToolSelections } = action.payload;
      return {
        ...state,
        selectorToolSelections,
      };
    }
    case types.SELECTOR_TOOL_CLEAR_ALL_SELECTIONS:
      return {
        ...state,
        selectorToolSelections: [],
      };
    case types.SELECTOR_TOOL_DISABLE:
      return {
        ...state,
        isSelectorToolActive: false,
        selectorToolSelections: [],
        selectorCumulativeInfo: {},
      };
    case types.SELECTOR_TOOL_ENABLE: {
      // clear selections when enabling the selector tool
      const nextSelectorToolSelections = state.isSelectorToolActive
        ? state.selectorToolSelections
        : [];
      return {
        ...state,
        isMeasurementToolActive: false,
        isSelectorToolActive: true,
        crossSectionPickMode: false,
        selectedObjects: [],
        selectorToolSelections: nextSelectorToolSelections,
        selectorCumulativeInfo: {},
      };
    }
    case types.SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO: {
      const { cumulativeInfo: selectorCumulativeInfo } = action.payload;
      return {
        ...state,
        selectorCumulativeInfo,
      };
    }
    case types.SELECTOR_TOOL_RECEIVE_SELECTION: {
      const { selection } = action.payload;
      return {
        ...state,
        selectorToolSelections: state.selectorToolSelections.concat([
          {
            ...selection,
            cadViewerState: getEncodedCADViewerState(state),
          },
        ]),
      };
    }
    case types.SELECTOR_TOOL_REMOVE_SELECTION: {
      const { f, obj } = action.payload;
      const isStep = obj.includes('|');
      let selectorToolSelections = [];
      if (isStep) {
        selectorToolSelections = state.selectorToolSelections.filter(
          ({ selectedObject }) => selectedObject.obj !== obj,
        );
      } else {
        // stls
        selectorToolSelections = state.selectorToolSelections.filter(
          ({ selectedObject }) => selectedObject.f !== f,
        );
      }
      return {
        ...state,
        selectorToolSelections,
      };
    }
    case types.SELECTOR_TOOL_TOGGLE: {
      // measurement tool and selector tool cannot both be active at the same time
      const nextSelectorToolState = !state.isSelectorToolActive;
      const nextMeasurementToolState = nextSelectorToolState
        ? false
        : state.isMeasurementToolActive;
      const nextSelectedObjects = nextSelectorToolState
        ? []
        : state.selectedObjects;

      return {
        ...state,
        isMeasurementToolActive: nextMeasurementToolState,
        isSelectorToolActive: nextSelectorToolState,
        crossSectionPickMode: false,
        selectedObjects: nextSelectedObjects,
        selectorToolSelections: [],
        caliperObjects: [],
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
        selectorCumulativeInfo: {},
      };
    }
    case types.SET_GEOMETRIC_INTERACTION_READY: {
      const { isReady: isGeometricInteractionReady } = action.payload;
      return {
        ...state,
        isGeometricInteractionReady,
      };
    }
    case types.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS: {
      const { partFileUuid: loadedPartFileUuid } = action.payload;
      return {
        ...state,
        viewerPendingPartFileUuids: state.viewerPendingPartFileUuids.includes(
          loadedPartFileUuid,
        )
          ? state.viewerPendingPartFileUuids.filter(
              (partFileUuid) => partFileUuid !== loadedPartFileUuid,
            )
          : state.viewerPendingPartFileUuids,
        viewerReadyPartFileUuids: state.viewerReadyPartFileUuids.includes(
          loadedPartFileUuid,
        )
          ? state.viewerReadyPartFileUuids
          : [...state.viewerReadyPartFileUuids, loadedPartFileUuid],
      };
    }
    case types.VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_END: {
      const { partFileUuid: partFileUuidToAdd } = action.payload;
      return state.viewerPendingPartFileUuids.includes(partFileUuidToAdd)
        ? state
        : {
            ...state,
            viewerPendingPartFileUuids: [
              ...state.viewerPendingPartFileUuids,
              partFileUuidToAdd,
            ],
          };
    }
    case types.VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_START: {
      const { partFileUuid: partFileUuidToAdd } = action.payload;
      return state.viewerPendingPartFileUuids.includes(partFileUuidToAdd)
        ? state
        : {
            ...state,
            viewerPendingPartFileUuids: [
              partFileUuidToAdd,
              ...state.viewerPendingPartFileUuids,
            ],
          };
    }
    case types.VIEWER_STATE_CAPTURE_NEXT_STATE_REQUEST:
      return {
        ...state,
        captureViewerState: true,
      };
    case types.VIEWER_STATE_RECEIVE:
      return {
        ...state,
        captureViewerState: false,
      };
    case types.VIEWER_STATE_SNAP_TO: {
      const { cadViewerState, viewerState } = action.payload;
      return {
        ...state,
        ...decodeEncodedCADViewerState(cadViewerState || {}),
        viewerState,
      };
    }
    case types.VIEWER_STATE_RESET:
      return {
        ...state,
        viewerState: null,
      };
    case types.WIRE_FRAME_TOOL_TOGGLE: {
      const nextWireFrame = !state.isWireFrameToolActive;
      return {
        ...state,
        isWireFrameToolActive: nextWireFrame,
        isHighlightViewActive: nextWireFrame
          ? false
          : state.isHighlightViewActive,
        crossSectionPickMode: nextWireFrame
          ? false
          : state.crossSectionPickMode,
      };
    }
    default:
      return state;
  }
};

const getScope = (state: RootState) =>
  state.modules.common.viewer.cadViewer.cadViewer;

export const getInterrogationInfo = (state: RootState) =>
  getScope(state).interrogationInfo;

export const getIsDimensionToolActive = (state: RootState) =>
  getScope(state).isDimensionToolActive;

export const getCrossSectionEnabled = (state: RootState) =>
  getScope(state).crossSectionEnabled;

export const getCrossSectionInverted = (state: RootState) =>
  getScope(state).crossSectionInverted;

export const getCrossSectionPickMode = (state: RootState) =>
  getScope(state).crossSectionPickMode;

export const getCrossSectionPosition = (state: RootState) =>
  getScope(state).crossSectionPosition;

export const getCrossSectionNormal = (state: RootState) =>
  getScope(state).crossSectionNormal;

export const getIsMeasurementToolActive = (state: RootState) =>
  getScope(state).isMeasurementToolActive;

export const getIsOptimalBoundingBoxToolActive = (state: RootState) =>
  getScope(state).isOptimalBoundingBoxToolActive;

export const getIsSelectorToolActive = (state: RootState) =>
  getScope(state).isSelectorToolActive;

export const getIsWireFrameToolActive = (state: RootState) =>
  getScope(state).isWireFrameToolActive;

export const getRenderNativeColors = (state: RootState) =>
  getScope(state).renderNativeColors;

export const getIsHighlightViewActive = (state: RootState) =>
  getScope(state).isHighlightViewActive;

export const getIsPMIViewActive = (state: RootState) => {
  const { isPMIViewActive, hasPMIAnnotations } = getScope(state);
  return hasPMIAnnotations && isPMIViewActive;
};

export const getHasPMIAnnotations = (state: RootState) =>
  getScope(state).hasPMIAnnotations;

export const getIsFocusedViewActive = (state: RootState) =>
  getScope(state).isFocusedViewActive;

export const getIsExplodedViewActive = (state: RootState) =>
  getScope(state).isExplodedViewToolActive;

export const getExplodedState = (state: RootState) =>
  getScope(state).explodedState;

export const getExplodedFactorForViewerId = (
  state: RootState,
  viewerId: string,
) => getScope(state).explodedState[viewerId];

export const getHighlight = (state: RootState) => getScope(state).highlight;

export const getMetaData = (state: RootState) => getScope(state).metaData;

export const getMeasurementComparison = (state: RootState) =>
  getScope(state).measurementComparison;

export const getNativeUnits = (state: RootState) => getScope(state).nativeUnits;

export const getDisplayUnits = (state: RootState) =>
  getScope(state).displayUnits;

export const getDimensionalPrecision = (state: RootState) =>
  getScope(state).dimensionalPrecision;

export const getDisplayWeightUnits = (state: RootState) =>
  getScope(state).displayWeightUnits;

export const getDisplayScale = (state: RootState) =>
  getScope(state).displayScale;

export const getSelectedFeatures = (state: RootState) =>
  getScope(state).selectedFeatures;

export const getSelectedManufacturabilityFeedback = (state: RootState) =>
  getScope(state).selectedManufacturabilityFeedback;

export const getSelectedObjects = (state: RootState) =>
  getScope(state).selectedObjects;

export const getSelectorCumulativeInfo = (state: RootState) =>
  getScope(state).selectorCumulativeInfo;

export const getSelectorToolSelections = (state: RootState) =>
  getScope(state).selectorToolSelections;

export const getAreSelectorToolSelectionsPresent = (state: RootState) =>
  getScope(state).selectorToolSelections.length > 0;

export const getCaliperObjects = (state: RootState) =>
  getScope(state).caliperObjects;

export const getCaliperToolSelections = (state: RootState) =>
  getScope(state).caliperToolSelections;

export const getAreCaliperToolSelectionsPresent = (state: RootState) =>
  getScope(state).caliperToolSelections.length > 0;

export const getCurrentMeasurementValue = (state: RootState) =>
  getScope(state).currentMeasurement;

export const getCapturedCaliperState = (state: RootState) =>
  getScope(state).capturedCaliperState;

export const getCapturedCaliperCADState = (state: RootState) =>
  getScope(state).capturedCaliperCADState;

export const getViewerState = (state: RootState) => getScope(state).viewerState;

export const shouldCaptureViewerState = (state: RootState) =>
  getScope(state).captureViewerState;

export const getActiveBodies = (state: RootState) =>
  getScope(state).activeBodies;

export const getActiveGeometricPartFileUuids = (state: RootState) =>
  getScope(state).activeGeometricPartFileUuids;

export const getSelectedBodies = (state: RootState) =>
  getScope(state).selectedBodies;

export const getFocusedBodies = (state: RootState) =>
  getScope(state).focusedBodies;

export const getIsBodyFocused = (state: RootState, viewerId: string) =>
  getFocusedBodies(state).includes(viewerId);

export const getPrevFocusedBodies = (state: RootState) =>
  getScope(state).prevFocusedBodies;

export const getShowDxfControls = (state: RootState) =>
  getScope(state).showDxfControls;

export const getVisibleBodies = (state: RootState) =>
  getScope(state).visibleBodies;

export const getTemporarilySimplified = (state: RootState) =>
  getScope(state).temporarilySimplified;

export const getTemporarilySimplifiedParts = (state: RootState) =>
  getScope(state).temporarilySimplifiedParts;

export const getTooComplexForPartViewer = (state: RootState) =>
  getScope(state).tooComplex;

export const getExpandedBodies = (state: RootState) =>
  getScope(state).expandedBodies;

export const getEnabledBodies = (state: RootState) =>
  getScope(state).enabledBodies;

export const getIsBodyEnabled = (state: RootState, viewerId: string) =>
  getExpandedBodies(state).includes(viewerId);

export const getHoverBody = (state: RootState) => getScope(state).hoverBody;

// TODO: REFACTOR TO getFocusedPartFileUuid
export const getFocusedUuid = (state: RootState) => getScope(state).focusedUuid;

export const getForceFocus = (state: RootState) => getScope(state).forceFocus;

export const getForceView = (state: RootState) => getScope(state).forceView;

export const getFeatureSnapTo = (state: RootState) =>
  getScope(state).featureSnapTo;

export const getShowAdditionalKeys = (state: RootState) =>
  getScope(state).showAdditionalKeys;

export const getShowAdditionalKeysExplicit = (state: RootState) =>
  getScope(state).showAdditionalKeysExplicit;

export const getIsInteractionReady = (state: RootState) =>
  getScope(state).isGeometricInteractionReady;

export const getEncodedCADState = (state: RootState) => {
  return getEncodedCADViewerState(getScope(state));
};

export const getMaterialStockResults = (state: RootState) =>
  getScope(state).materialStockResults;

export const getVisibleStockResultKeys = (state: RootState) =>
  getScope(state).visibleStockResultKeys;

export const getViewerPendingPartFileUuids = (state: RootState) =>
  getScope(state).viewerPendingPartFileUuids;

export const getIsViewerReadyForPartFile = (
  state: RootState,
  partFileUuid: string,
) => getScope(state).viewerReadyPartFileUuids.includes(partFileUuid);

export default reducer;
